import { useCartPricing, useCartPricingError } from '../../contexts/CartPricingContext';
import { useLocale } from '../../contexts/LocaleContext';
import useHardwareFirstAccessoriesInCents from '../../hooks/useHardwareFirstAccessoriesInCents';
import useHardwareFirstTrialUnits from '../../hooks/useHardwareFirstTrialUnits';
import { formatHardwarePrice } from '../../lib/hardwareFirst';
import { dollarsToCents } from '../../lib/util/money';
import { HardwareFirstGiftTermMonths } from '../../lib/hardwareFirstGiftPlans';
import { useSelectedGiftPlanTerm } from '../HardwareFirstGift/useHardwareFirstGiftPricing';

interface HardwareFirstTotals {
  /** Units across every repriced hardware item and trial unit, for the "Fi Trackers (n)" label. */
  trackerCount: number;
  /** List price of the hardware before any discount, trial units included. */
  hardwareInCents: number;
  /** List price of the non-tracker lines, e.g. a band; 0 without any. */
  accessoriesInCents: number;
  /** the amount gift cards, listed apart from the accessories; 0 without one */
  giftCardInCents: number;
  /** List price of the trial units, shown as taken off while they are free; 0 without a trial. */
  trialInCents: number;
  /** Everything taken off the hardware - the arm's own repricing plus any coupon. */
  discountInCents: number;
  /** The flat per-tracker multi-device saving summed over the cart; part of `discountInCents`. */
  multiDeviceDiscountInCents: number;
  /** The rest of `discountInCents`: the promo code's share plus anything a coupon added. */
  promoDiscountInCents: number;
  /** Whole percent the code takes off the hardware, for the pill; zero without a code. */
  promoPercentOff: number;
  /** the gift box's prepaid plan, intro price over every gifted tracker; 0 when the cart is not a gift */
  giftPlanInCents: number;
  /** the same plan at list, struck beside `giftPlanInCents` */
  giftPlanListInCents: number;
  giftTermMonths?: HardwareFirstGiftTermMonths;
  /** everything a strikethrough counts: the hardware discounts plus the gift plan's intro price under its list */
  savingInCents: number;
  /** Pre-tax, matching the rows above it; the summary defers tax to checkout. */
  totalInCents: number;
  formatPrice: (cents: number) => string;
}

/**
 * The hardware-first cart's receipt figures. The discount is derived from the hardware list price
 * and the server total rather than summed from the individual discount sources, so the column the
 * shopper reads always adds up even when a coupon stacks on top of the arm's repricing.
 *
 * Everything here is pre-tax. The summary lists hardware, discount and shipping and then says taxes
 * are calculated at checkout, so a tax-inclusive total would not equal the rows above it - the
 * server includes tax in `totalInCents` whenever it priced the cart with it.
 */
export default function useHardwareFirstTotals(): HardwareFirstTotals {
  const cartPricing = useCartPricing();
  const pricingError = useCartPricingError();
  const { count: trialCount, listPriceInCents: trialInCents } = useHardwareFirstTrialUnits();
  const { accessoriesInCents, giftCardInCents } = useHardwareFirstAccessoriesInCents();
  const locale = useLocale();
  const selectedGiftTerm = useSelectedGiftPlanTerm();
  const { hardwareDiscounts, hardwareFirstMultiDeviceDiscountInCents, totalInCents, shippingInCents, taxInCents } =
    cartPricing;

  const discounts = Object.values(hardwareDiscounts ?? {});
  const trackerCount = discounts.length + trialCount;
  // the trial units are not in `hardwareDiscounts` and cost nothing today, so the discount derivation
  // below runs on the repriced units alone and the trial rides on top as its own list-price row
  const repricedHardwareInCents = discounts.reduce((sum, discount) => sum + discount.listPriceInCents, 0);
  const hardwareInCents = repricedHardwareInCents + trialInCents;
  // the plan comes off web's own lines, so pricing from before the gift was ticked lists no plan to back-solve against
  const giftPlanLines = (cartPricing.lineItems ?? []).filter(({ sku }) => sku.startsWith('hf-gift-plan-'));
  const giftTerm = !pricingError && giftPlanLines.length > 0 ? selectedGiftTerm : undefined;
  const giftPlanInCents = giftTerm
    ? giftPlanLines.reduce((sum, line) => sum + dollarsToCents(line.subtotalInDollars), 0)
    : 0;
  const preTaxTotalInCents = Math.max(totalInCents - (taxInCents ?? 0), 0);
  const chargesInCents =
    repricedHardwareInCents + accessoriesInCents + giftCardInCents + giftPlanInCents + (shippingInCents ?? 0);
  // stale pricing after a failed refetch does not match the cart, so its gap is no saving
  const discountInCents = pricingError ? 0 : Math.max(chargesInCents - preTaxTotalInCents, 0);
  const multiDeviceDiscountInCents = Math.min(hardwareFirstMultiDeviceDiscountInCents ?? 0, discountInCents);
  const giftPlanListInCents = (giftTerm?.listAmountInCents ?? 0) * discounts.length;

  return {
    trackerCount,
    hardwareInCents,
    accessoriesInCents,
    giftCardInCents,
    trialInCents,
    discountInCents,
    multiDeviceDiscountInCents,
    promoDiscountInCents: discountInCents - multiDeviceDiscountInCents,
    promoPercentOff:
      repricedHardwareInCents > 0
        ? Math.round(((discountInCents - multiDeviceDiscountInCents) / repricedHardwareInCents) * 100)
        : 0,
    giftPlanInCents,
    giftPlanListInCents,
    giftTermMonths: giftTerm?.months,
    savingInCents: discountInCents + Math.max(giftPlanListInCents - giftPlanInCents, 0),
    totalInCents: preTaxTotalInCents,
    formatPrice: (cents: number) => formatHardwarePrice(cents, locale),
  };
}
